import * as React from 'react'

import { Button, Flex, Skeleton, Text, mergeRefs } from '@buffer-mono/popcorn'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import { Sidepanel } from '../../components/Sidepanel'
import { ConversationCard } from '../ConversationCard'
import type { UseConversationsQueryReturn } from '../hooks/useConversationsQuery'
import type { DMsChannel } from '../types'

import styles from './ConversationsSidepanel.module.css'

import type { JSX } from 'react'

type ConversationsSidepanelProps = Pick<
  UseConversationsQueryReturn,
  'conversations' | 'loading' | 'error' | 'fetchingMore' | 'lastElementRef'
> & {
  /** The page's channels by id, for each card's channel line. */
  channelById: ReadonlyMap<string, DMsChannel>
  selectedConversationId: string | undefined
  onSelectConversation: (conversationId: string) => void
  onResolveConversation: (conversationId: string) => void
  onUnresolveConversation: (conversationId: string) => void
  onRetry: () => void
}

/**
 * Left pane of the Messages view: the conversation list.
 *
 * Deliberately not collapsible. The list is the view's index, so collapsing it
 * would leave the thread pane with no way back.
 *
 * Takes the query result as props rather than running the query itself, so
 * the view owns one data source for the list and the thread, and stories and
 * tests can drive every state without Apollo.
 */
export function ConversationsSidepanel({
  conversations,
  loading,
  error,
  fetchingMore,
  lastElementRef,
  channelById,
  selectedConversationId,
  onSelectConversation,
  onResolveConversation,
  onUnresolveConversation,
  onRetry,
}: ConversationsSidepanelProps): JSX.Element {
  return (
    <Sidepanel label="Conversations" size="large">
      <ConversationsHeader />
      <Sidepanel.Content>
        <ConversationsList
          conversations={conversations}
          loading={loading}
          error={error}
          fetchingMore={fetchingMore}
          lastElementRef={lastElementRef}
          channelById={channelById}
          selectedConversationId={selectedConversationId}
          onSelectConversation={onSelectConversation}
          onResolveConversation={onResolveConversation}
          onUnresolveConversation={onUnresolveConversation}
          onRetry={onRetry}
        />
      </Sidepanel.Content>
    </Sidepanel>
  )
}

/**
 * The panel's title row, per the Figma `_conversations panel`: a subtle
 * "Conversations" label. The design reserves the trailing end for a sync
 * indicator, which arrives with the DM sync work.
 *
 * Laid out as a bare row rather than `Sidepanel.Header`, matching the by-post
 * panel: that keeps the two panel headers the same height and leaves out the
 * separator, which `Sidepanel.Header` would draw.
 */
function ConversationsHeader(): JSX.Element {
  return (
    <Flex align="center" justify="between" fullWidth className={styles.header}>
      <Text weight="medium" color="subtle" lineHeight="tight">
        Conversations
      </Text>
    </Flex>
  )
}

function ConversationsList({
  conversations,
  loading,
  error,
  fetchingMore,
  lastElementRef,
  channelById,
  selectedConversationId,
  onSelectConversation,
  onResolveConversation,
  onUnresolveConversation,
  onRetry,
}: ConversationsSidepanelProps): JSX.Element {
  const selectedItemRef = useScrollToSelected(
    selectedConversationId,
    conversations.length,
  )

  if (error) {
    return <ConversationsError onRetry={onRetry} />
  }

  if (loading && conversations.length === 0) {
    return <ConversationsSkeleton />
  }

  if (conversations.length === 0) {
    return <ConversationsEmpty />
  }

  return (
    <Sidepanel.List aria-label="Conversations">
      {conversations.map((conversation, index) => {
        const isSelected = conversation.id === selectedConversationId
        const isLast = index === conversations.length - 1
        const ref =
          isSelected || isLast
            ? mergeRefs(
                isSelected ? selectedItemRef : undefined,
                isLast ? lastElementRef : undefined,
              )
            : undefined

        return (
          <ConversationCard
            key={conversation.id}
            ref={ref}
            conversation={conversation}
            channel={channelById.get(conversation.channelId)}
            isSelected={isSelected}
            onSelect={onSelectConversation}
            onResolve={onResolveConversation}
            onUnresolve={onUnresolveConversation}
          />
        )
      })}
      {fetchingMore && <ConversationCardSkeleton />}
    </Sidepanel.List>
  )
}

/**
 * Brings the selected card into view once per selection, so a conversation
 * opened from a link is visible without scrolling the list by hand.
 *
 * Re-runs when the row count changes as well as when the selection does: a
 * deep-linked id is set before the first page arrives, and its card only
 * exists to scroll to once the rows have rendered.
 */
function useScrollToSelected(
  selectedId: string | undefined,
  rowCount: number,
): React.RefObject<HTMLLIElement | null> {
  const selectedItemRef = React.useRef<HTMLLIElement | null>(null)
  const lastScrolledIdRef = React.useRef<string | null>(null)

  React.useLayoutEffect(() => {
    if (!selectedId || !selectedItemRef.current) return
    if (lastScrolledIdRef.current === selectedId) return
    lastScrolledIdRef.current = selectedId
    selectedItemRef.current.scrollIntoView({ block: 'nearest' })
  }, [selectedId, rowCount])

  return selectedItemRef
}

function ConversationsSkeleton(): JSX.Element {
  return (
    <Sidepanel.List aria-label="Loading conversations">
      {getSlotIds('conversation-loading-skeleton', 6).map((slotId) => (
        <ConversationCardSkeleton key={slotId} />
      ))}
    </Sidepanel.List>
  )
}

/** Mirrors a card's two rows so the list does not jump when rows arrive. */
function ConversationCardSkeleton(): JSX.Element {
  return (
    <Sidepanel.ListItem className={styles.skeletonCard}>
      <Skeleton height="0.75rem" width="40%" rounded />
      <Flex align="center" gap="xs">
        <Skeleton width="40px" height="40px" rounded />
        <Flex direction="column" gap="xs" className={styles.skeletonText}>
          <Skeleton height="0.875rem" width="60%" rounded />
          <Skeleton height="0.75rem" width="85%" rounded />
        </Flex>
      </Flex>
    </Sidepanel.ListItem>
  )
}

function ConversationsEmpty(): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="xs"
      className={styles.empty}
    >
      <Text weight="medium">No conversations yet</Text>
      <Text color="subtle" align="center" size="sm">
        Direct messages will show up here.
      </Text>
    </Flex>
  )
}

function ConversationsError({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="sm"
      className={styles.empty}
      role="alert"
    >
      <Text weight="medium">Conversations could not be loaded</Text>
      <Text color="subtle" align="center" size="sm">
        Check your connection and try again.
      </Text>
      <Button variant="secondary" size="small" onClick={onRetry}>
        Try Again
      </Button>
    </Flex>
  )
}
